import React, { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Routes, useLocation, useNavigationType } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import useReducedMotion from './useReducedMotion';
import './motion.css';

const tabs = ['/', '/talents-badges', '/islamic', '/more'];
const normalizePath = (path) => path === '/post' ? '/' : path;

export function navigationMotion(from, to, action, previousIndex, nextIndex, direction = 'ltr') {
  const previousTab = tabs.indexOf(normalizePath(from));
  const nextTab = tabs.indexOf(normalizePath(to));
  const rtl = direction === 'rtl' ? -1 : 1;
  if (previousTab >= 0 && nextTab >= 0) {
    const sign = Math.sign(nextTab - previousTab) * rtl;
    return { enter: `${sign * 14}%`, exit: `${sign * -6}%` };
  }
  const back = action === 'POP' && !(nextIndex > previousIndex);
  return back
    ? { enter: `${rtl * -6}%`, exit: `${rtl * 14}%` }
    : { enter: `${rtl * 14}%`, exit: `${rtl * -6}%` };
}

/**
 * Delay only the rendered route until the browser has captured the old screen.
 * Routes supplies the displayed location to the whole layout (including Home),
 * without remounting the persistent feed or transforming fixed-position DOM.
 */
export default function AnimatedRoutes({ children }) {
  const location = useLocation();
  const action = useNavigationType();
  const { resolvedDirection } = useLanguage();
  const reduced = useReducedMotion();
  const [displayed, setDisplayed] = useState(location);
  const last = useRef({ location, index: window.history.state?.idx });
  const surface = useRef(null);
  const nativeTransition = useRef(null);

  useEffect(() => {
    if (last.current.location === location) return undefined;
    const previous = last.current;
    const nextIndex = window.history.state?.idx;
    let cancelled = false;
    let committed = false;
    let fallingBack = false;
    const animations = [];
    const commit = () => {
      if (cancelled || committed) return;
      committed = true;
      last.current = { location, index: nextIndex };
      setDisplayed(location);
    };

    // Search, hash, modal history entries and Home aliases are not new screens.
    if ((action === 'REPLACE' && location.state?.__amposterCanonicalFrom === previous.location.pathname)
        || normalizePath(previous.location.pathname) === normalizePath(location.pathname)
        || reduced) {
      commit();
      return undefined;
    }

    const offsets = navigationMotion(previous.location.pathname, location.pathname,
      action, previous.index, nextIndex, resolvedDirection);
    const animate = (frames, duration) => {
      const animation = surface.current?.animate?.(frames, { duration, easing: 'ease-out' });
      if (animation) animations.push(animation);
      return animation?.finished?.catch(() => {});
    };
    const fallback = async () => {
      if (cancelled || fallingBack) return;
      fallingBack = true;
      if (!committed) await animate([{ opacity: 1 }, { opacity: .25 }], 110);
      if (cancelled) return;
      flushSync(commit);
      await animate([{ opacity: .25 }, { opacity: 1 }], 210);
    };
    if (!document.startViewTransition) {
      void fallback();
      return () => { cancelled = true; animations.forEach(animation => animation.cancel()); };
    }
    const root = document.documentElement;
    root.dataset.appNavigation = 'active';
    root.style.setProperty('--app-page-enter', offsets.enter);
    root.style.setProperty('--app-page-exit', offsets.exit);
    let transition;
    const cleanup = () => {
      if (transition && nativeTransition.current !== transition) return;
      nativeTransition.current = null;
      delete root.dataset.appNavigation;
      root.style.removeProperty('--app-page-enter');
      root.style.removeProperty('--app-page-exit');
    };
    try {
      transition = document.startViewTransition(() => {
        if (cancelled) return undefined;
        // Commit immediately, including Suspense's loading UI. Waiting for a
        // chunk here freezes the browser snapshot before motion can even begin.
        flushSync(commit);
        return undefined;
      });
      nativeTransition.current = transition;
      // Skipped snapshots (hidden tabs, rapid navigation) must still navigate.
      transition.ready.catch(() => { void fallback(); });
      transition.updateCallbackDone.catch(() => { void fallback(); });
      transition.finished.then(cleanup, cleanup);
    } catch {
      cleanup();
      void fallback();
    }
    return () => {
      cancelled = true;
      animations.forEach(animation => animation.cancel());
      transition?.skipTransition();
      cleanup();
    };
  }, [location, action, reduced, resolvedDirection]);

  return <div ref={surface} className="route-page-animator"><Routes location={displayed}>{children}</Routes></div>;
}
